import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  ApprovalChain, ApprovalEntityType,
  getProjectApprovalChains, getChainTemplateStages,
  createChainWithStages, updateChain, deleteChain,
  ChainStageTemplate,
} from '../../services/approvalService';
import PeoplePicker, { Person } from '../../components/PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string }

const ENTITY_TYPE_LABEL: Record<number, string> = {
  [ApprovalEntityType.Submittal]: 'Submittal',
  [ApprovalEntityType.RFI]:       'RFI',
  [ApprovalEntityType.PunchItem]: 'Punch Item',
  [ApprovalEntityType.Custom]:    'Custom',
};

export default function ApprovalWorkflowsSection({ pca, projectId }: Props) {
  const [chains, setChains] = useState<ApprovalChain[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ApprovalChain | null>(null);
  const [showNew, setShowNew] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setChains(await getProjectApprovalChains(pca, projectId)); }
    finally { setLoading(false); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const toggleActive = async (c: ApprovalChain) => {
    const next = !c.dagrun_pm_isactive;
    setChains(prev => prev.map(x => x.dagrun_pm_approvalchainid === c.dagrun_pm_approvalchainid ? { ...x, dagrun_pm_isactive: next } : x));
    try { await updateChain(pca, c.dagrun_pm_approvalchainid, { dagrun_pm_isactive: next }); }
    catch { reload(); }
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="eyebrow">APPROVAL WORKFLOWS</span>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >+ Add Workflow</button>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : chains.length === 0 ? (
        <div style={{ fontWeight: 300, fontSize: 13, color: 'var(--ink-3)' }}>
          No approval workflows configured. Submittals, RFIs, and punch items will not require multi-stage approval until you add one.
        </div>
      ) : (
        chains.map(c => (
          <div key={c.dagrun_pm_approvalchainid}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
            <strong style={{ flex: 1, fontSize: 13, color: 'var(--ink)' }}>{c.dagrun_pm_name}</strong>
            <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
              {c.dagrun_pm_entitytype != null ? ENTITY_TYPE_LABEL[c.dagrun_pm_entitytype] : '—'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
              {c.dagrun_pm_stages ?? 0} stage{c.dagrun_pm_stages === 1 ? '' : 's'}
            </span>
            <label style={{ fontSize: 12, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={!!c.dagrun_pm_isactive} onChange={() => toggleActive(c)} />
              active
            </label>
            <button type="button" onClick={() => setEditing(c)}
              style={{ background: 'transparent', border: 'none', color: 'var(--brand)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font-ui)' }}
            >Edit</button>
          </div>
        ))
      )}

      {showNew && (
        <ChainEditorModal pca={pca} projectId={projectId}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); reload(); }}
        />
      )}
      {editing && (
        <ChainEditorModal pca={pca} projectId={projectId} chain={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

interface ChainEditorProps {
  pca: IPublicClientApplication;
  projectId: string;
  chain?: ApprovalChain;
  onClose: () => void;
  onSaved: () => void;
}

interface DraftStage extends ChainStageTemplate { tempId: string }
let nextId = 1;
const newDraftStage = (): DraftStage => ({ tempId: `t${nextId++}`, label: '', approverid: '', approvername: '' });

function ChainEditorModal({ pca, projectId, chain, onClose, onSaved }: ChainEditorProps) {
  const [name, setName] = useState(chain?.dagrun_pm_name ?? '');
  const [entitytype, setEntitytype] = useState<number>(chain?.dagrun_pm_entitytype ?? ApprovalEntityType.Submittal);
  const [isactive, setIsactive] = useState<boolean>(chain?.dagrun_pm_isactive ?? true);
  const [stages, setStages] = useState<DraftStage[]>([newDraftStage()]);
  const [loadingStages, setLoadingStages] = useState(!!chain);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing stages when editing.
  useEffect(() => {
    if (!chain) return;
    let cancelled = false;
    getChainTemplateStages(pca, chain.dagrun_pm_approvalchainid)
      .then((templ) => {
        if (cancelled) return;
        if (templ.length > 0) {
          setStages(templ.map(s => ({
            tempId: s.dagrun_pm_approvalstageid,
            label: s.dagrun_pm_name,
            approverid: s.dagrun_pm_approverid ?? '',
            approvername: s.dagrun_pm_approvername ?? '',
          })));
        }
      })
      .finally(() => { if (!cancelled) setLoadingStages(false); });
    return () => { cancelled = true; };
  }, [pca, chain]);

  const addStage = () => {
    if (stages.length >= 5) return;
    setStages(s => [...s, newDraftStage()]);
  };
  const removeStage = (tempId: string) => {
    setStages(s => s.length > 1 ? s.filter(x => x.tempId !== tempId) : s);
  };
  const updateStage = (tempId: string, patch: Partial<DraftStage>) => {
    setStages(s => s.map(x => x.tempId === tempId ? { ...x, ...patch } : x));
  };
  const setApprover = (tempId: string, p: Person | null) => {
    updateStage(tempId, { approverid: p?.aadObjectId ?? '', approvername: p?.displayName ?? '' });
  };

  const canSave = name.trim() && stages.every(s => s.approverid && s.approvername);

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true); setError(null);
    try {
      if (chain) {
        // Simplest edit model: update chain metadata and replace all template
        // stages. Chain-instance stages attached to live entities keep their
        // own copies, so changing the template doesn't retroactively alter
        // in-flight approvals (desired behaviour).
        await updateChain(pca, chain.dagrun_pm_approvalchainid, {
          dagrun_pm_name: name.trim(),
          dagrun_pm_entitytype: entitytype,
          dagrun_pm_stages: stages.length,
          dagrun_pm_isactive: isactive,
        });
        // Delete existing templates, then recreate. Only delete templates
        // (entityid null) — don't touch instance stages.
        const existingTemplates = await getChainTemplateStages(pca, chain.dagrun_pm_approvalchainid);
        for (const t of existingTemplates) {
          try { const { deleteStage } = await import('../../services/approvalService'); await deleteStage(pca, t.dagrun_pm_approvalstageid); }
          catch { /* best effort */ }
        }
        // Recreate by calling createChainWithStages would duplicate the
        // chain — inline the stage creation instead.
        const { dvCreate } = await import('../../api/dataverseClient');
        for (let i = 0; i < stages.length; i++) {
          const s = stages[i];
          await dvCreate(pca, 'dagrun_pm_approvalstages', {
            dagrun_pm_name: s.label.trim() || `Stage ${i + 1}`,
            dagrun_pm_approvalchainid: chain.dagrun_pm_approvalchainid,
            dagrun_pm_stageorder: i + 1,
            dagrun_pm_approverid: s.approverid,
            dagrun_pm_approvername: s.approvername,
            dagrun_pm_status: 1, // Pending
          });
        }
      } else {
        await createChainWithStages(pca, projectId, {
          name: name.trim(),
          entitytype,
          isactive,
          stages: stages.map(s => ({
            label: s.label.trim() || `Stage`,
            approverid: s.approverid,
            approvername: s.approvername,
          })),
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!chain || saving) return;
    setSaving(true);
    try {
      await deleteChain(pca, chain.dagrun_pm_approvalchainid);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title={chain ? 'Edit Approval Workflow' : 'Add Approval Workflow'} onClose={onClose} width={600}
      footer={<>
        {chain && <BMDangerButton onClick={() => setConfirmDelete(true)} disabled={saving}>Delete</BMDangerButton>}
        <div style={{ flex: 1 }} />
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !canSave}>
          {saving ? 'Saving…' : chain ? 'Save Workflow' : 'Create Workflow'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Workflow Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. Engineering Submittal Review" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Applies To</label>
          <select value={entitytype} onChange={e => setEntitytype(Number(e.target.value))} style={BM_INPUT}>
            {[ApprovalEntityType.Submittal, ApprovalEntityType.RFI, ApprovalEntityType.PunchItem, ApprovalEntityType.Custom].map(v =>
              <option key={v} value={v}>{ENTITY_TYPE_LABEL[v]}</option>)}
          </select>
        </div>
        <div style={{ alignSelf: 'end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
            <input type="checkbox" checked={isactive} onChange={e => setIsactive(e.target.checked)} />
            Active — auto-instantiate for new items
          </label>
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
        Stages ({stages.length} / 5)
      </div>
      {loadingStages ? (
        <div style={{ fontSize: 12, color: '#9198A8' }}>Loading stages…</div>
      ) : (
        <>
          {stages.map((s, idx) => (
            <div key={s.tempId} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10,
              padding: 10, background: '#F5F6F8', borderRadius: 6,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14,
                background: '#0E7490', color: '#fff',
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
              }}>{idx + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 6 }}>
                  <input value={s.label} onChange={e => updateStage(s.tempId, { label: e.target.value })}
                    placeholder="Stage name (optional) — e.g. Engineering Review"
                    style={{ ...BM_INPUT, fontSize: 13 }} />
                </div>
                <PeoplePicker pca={pca}
                  value={s.approverid ? { aadObjectId: s.approverid, displayName: s.approvername || s.approverid } : null}
                  onChange={p => setApprover(s.tempId, p)}
                  placeholder="Select approver…"
                />
              </div>
              {stages.length > 1 && (
                <button type="button" onClick={() => removeStage(s.tempId)} aria-label="Remove stage"
                  style={{ background: 'transparent', border: 'none', color: '#9198A8', cursor: 'pointer', fontSize: 16, lineHeight: 1, marginTop: 4 }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#9198A8')}
                >×</button>
              )}
            </div>
          ))}
          {stages.length < 5 && (
            <button type="button" onClick={addStage}
              style={{ background: 'transparent', color: '#0E7490', border: '1px dashed #0E7490', borderRadius: 6, padding: '6px 12px', fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' }}
            >+ Add stage</button>
          )}
        </>
      )}

      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}

      {confirmDelete && chain && (
        <BoardModal open title="Delete Workflow" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete Workflow'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{chain.dagrun_pm_name}</strong> and all its template stages?
          In-flight approval stages already attached to submittals/RFIs will remain but will no longer advance
          against the (now-missing) chain template.
        </BoardModal>
      )}
    </BoardModal>
  );
}
